import React from 'react';
import styled, {keyframes} from 'styled-components';
import {useBreakpointDown} from 'epic-ue-theme';
import {useInView} from 'react-intersection-observer';

const defaultHeight = 144;
const smallHeight = 80;

const scrollEffect = (height: number) => keyframes`
    0% {
        top: 0;
        height: 0;
    }
    40% {
        top: 0;
        height: ${height}px;
    }
    85% {
        top: ${height}px;
        height: 0;
    }
    100% {
        top: ${height}px;
        height: 0;
    }

`;

interface HeightProps {
    height: number;
}

const ScrollIndicatorElem = styled.div<HeightProps & {animationState: string; alignLeft?: boolean}>`
    width: 2px;
    margin: ${(p) => (p.alignLeft ? 'initial' : '0 auto')};
    height: ${(p) => p.height / 2}px;
    position: relative;
    text-align: center;
    > span {
        content: '';
        display: block;
        position: absolute;
        top: -${(p) => p.height / 2}px;
        left: 0;
        transform: translateZ(0, 0, 0);
        animation-name: ${(p) => scrollEffect(p.height)};
        animation-duration: 2s;
        animation-iteration-count: infinite;
        animation-timing-function: ${(p) => p.theme.easing};
        animation-play-state: ${(p) => p.animationState};
        width: 2px;
        height: 0;
        background: ${(p) =>
            p.theme.ifLight(p, p.theme.palette.common.black, p.theme.palette.common.white)};
    }
`;

const getHeight = (isSm: boolean) => {
    return isSm ? smallHeight : defaultHeight;
};

interface ScrollIndicatorProps {
    forceSmall?: boolean;
    width?: string;
    alignLeft?: boolean;
}

export const ScrollIndicator = ({forceSmall, alignLeft}: ScrollIndicatorProps): JSX.Element => {
    const {inView, ref} = useInView();
    const isSm = useBreakpointDown('sm');
    const height = forceSmall ? smallHeight : getHeight(isSm);

    return (
        <ScrollIndicatorElem
            ref={ref}
            animationState={inView ? 'running' : 'paused'}
            className="scroll-indicator"
            height={height}
            alignLeft={alignLeft}>
            <span />
        </ScrollIndicatorElem>
    );
};

const AboveFoldHintStyled = styled.div<AboveFoldScrollIndicatorProps & HeightProps>`
    position: relative;
    transform: translateY(${(p) => (p.translateY !== undefined ? p.translateY : -100)}%);
    z-index: 1;
    min-height: ${(p) => p.height / 2}px;
`;

interface AboveFoldScrollIndicatorProps {
    translateY?: number;
    forceSmall?: boolean;
    width?: string;
    alignLeft?: boolean;
}

export const AboveFoldScrollIndicator = ({
    translateY,
    forceSmall = false,
    alignLeft = false
}: AboveFoldScrollIndicatorProps): JSX.Element => {
    return (
        <AboveFoldHintStyled
            className="above-fold-hint"
            translateY={translateY}
            height={smallHeight}>
            <ScrollIndicator forceSmall={forceSmall} alignLeft={alignLeft} />
        </AboveFoldHintStyled>
    );
};
